import type { MatchLiveStatus, NameResolver } from './BracketView';
import { RoundRobinMatch, RoundRobinMatchResult, BYE_USER_ID } from './RoundRobinScheduler';
import { avatarDataUriForName } from '../avatar';

type PlayerColumnArgs = {
  userId: string;
  name: string;
  isBye: boolean;
  resolveAvatar?: (userId?: string | null) => string | undefined;
  resolveTournamentPPR?: (userId?: string | null) => number | undefined;
};

export type RoundRobinScheduleOptions = {
  currentUserId?: string;
  resolveName?: NameResolver;
  onMatchClick?: (match: RoundRobinMatch) => void;
  onPlayerProfile?: (userId: string, userName: string) => void;
  highlightMatchIndex?: number;
  resolveAvatar?: (userId?: string | null) => string | undefined;
  resolveTournamentPPR?: (userId?: string | null) => number | undefined;
  liveStatuses?: Record<string, MatchLiveStatus | undefined>;
  playerRecords?: Record<string, { wins: number; losses: number }>;
};

export class RoundRobinScheduleView {
  private root?: HTMLDivElement;
  private roundContainer?: HTMLDivElement;
  private roundElements: HTMLDivElement[] = [];
  private indicatorButtons: HTMLButtonElement[] = [];
  private activeRoundIndex = 0;
  private containerPaddingLeft = 0;
  private savedScrollLeft = 0;
  private playerProfileHandler?: (userId: string, userName: string) => void;
  private roundScrollHandler = () => {
    if (!this.roundContainer || !this.roundElements.length) return;
    const { scrollLeft, clientWidth } = this.roundContainer;
    const viewportCenter = scrollLeft + clientWidth / 2;
    let closestIdx = 0;
    let closestDiff = Infinity;
    this.roundElements.forEach((el, idx) => {
      const roundCenter = (el.offsetLeft - this.containerPaddingLeft) + el.clientWidth / 2;
      const diff = Math.abs(roundCenter - viewportCenter);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIdx = idx;
      }
    });
    this.setActiveRound(closestIdx);
  };

  public mount(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'tourney-schedule-viewport';
    parent.appendChild(this.root);
    this.root.addEventListener('click', this.onPlayerProfileClick, true);
  }

  public render(matches: RoundRobinMatch[], options: RoundRobinScheduleOptions = {}) {
    if (!this.root) return;
    this.playerProfileHandler = options.onPlayerProfile;
    const resolver = options.resolveName ?? ((id) => id ?? 'Unknown');
    const grouped = groupByRound(matches);
    const roundsMarkup = grouped.map(([round, rows]) => `
      <div class="tourney-schedule-round" data-round="${round}">
        <div class="tourney-schedule-round-title">Round ${round}</div>
        <div class="tourney-schedule-round-body">
          ${rows.map(match => this.renderMatch(match, resolver, options)).join('')}
        </div>
      </div>
    `).join('');
    const indicatorsMarkup = grouped.map(([,], idx) => `
      <button class="tourney-schedule-carousel-dot${idx === 0 ? ' active' : ''}" type="button" data-round-index="${idx}"></button>
    `).join('');

    // Save scroll position before cleanup
    this.saveScrollPosition();
    this.cleanupCarousel();
    this.root.innerHTML = `
      <div class="tourney-schedule-container">
        <div class="tourney-schedule-header">Schedule</div>
        <div class="tourney-schedule-rounds">
          ${roundsMarkup}
        </div>
        <div class="tourney-schedule-carousel-indicators">
          ${indicatorsMarkup}
        </div>
      </div>
    `;

    if (options.onMatchClick) {
      this.root.querySelectorAll<HTMLElement>('[data-match-index]').forEach(el => {
        const idx = Number(el.dataset.matchIndex);
        const match = matches.find(m => m.matchIndex === idx);
        if (!match) return;
        if (match.playerA.userId === BYE_USER_ID || match.playerB.userId === BYE_USER_ID) return;
        el.onclick = () => options.onMatchClick?.(match);
      });
    }
    this.setupCarousel();
    // Restore scroll position after setup
    this.restoreScrollPosition();
  }

  private renderMatch(match: RoundRobinMatch, resolver: NameResolver, options: RoundRobinScheduleOptions) {
    const matchClasses = [
      'tourney-schedule-match',
      match.result ? 'completed' : 'pending',
      options.highlightMatchIndex === match.matchIndex ? 'highlighted' : '',
      (match.playerA.userId === options.currentUserId || match.playerB.userId === options.currentUserId) ? 'current-user' : ''
    ].filter(Boolean).join(' ');
    const { html, isLiveOrCompleted, statusLabel } = this.buildMatchInner(match, resolver, options);
    const metaHtml = statusLabel ? `
        <div class="tourney-schedule-meta">
          <span>${statusLabel}</span>
        </div>` : '';
    return `
      <div class="${matchClasses}${isLiveOrCompleted ? ' live' : ''}" data-match-index="${match.matchIndex}">
        ${html}
        ${metaHtml}
      </div>
    `;
  }

  private renderMatchContent(match: RoundRobinMatch, resolver: NameResolver, options: RoundRobinScheduleOptions): string {
    return this.buildMatchInner(match, resolver, options).html;
  }

  private buildMatchInner(match: RoundRobinMatch, resolver: NameResolver, options: RoundRobinScheduleOptions) {
    const playerAName = formatPlayer(match.playerA, resolver);
    const playerBName = formatPlayer(match.playerB, resolver);
    const isByeA = match.playerA.userId === BYE_USER_ID;
    const isByeB = match.playerB.userId === BYE_USER_ID;
    const isByeMatch = isByeA || isByeB;
    const completed = !!match.result;
    const result = match.result;
    const statusKey = `${match.round}:${match.matchIndex}`;
    const liveStatus = options.liveStatuses?.[statusKey];
    const showLiveInfo = Boolean(liveStatus && !liveStatus.isFinal && liveStatus.phase !== 'over');
    const isSuddenDeathLive = showLiveInfo && liveStatus?.phase === 'sudden_death';
    const roundNumber = liveStatus?.round ?? 0;
    const roundText = showLiveInfo
      ? (isSuddenDeathLive ? 'Round SD' : (roundNumber ? `Round ${roundNumber}` : ''))
      : '';
    const ballsLeft = showLiveInfo ? Math.max(0, Math.min(liveStatus!.ballsLeft ?? 0, 12)) : 0;
    const ballDisplay = ballsLeft > 0 ? Array(ballsLeft).fill('⚪').join(' ') : '';
    const playerRecord = (userId: string) => {
      if (userId === BYE_USER_ID) return '';
      const entry = options.playerRecords?.[userId];
      return entry ? `${entry.wins}-${entry.losses}` : '0-0';
    };
    const avatarFor = (userId: string, fallbackName: string) => {
      if (userId === BYE_USER_ID) return undefined;
      return options.resolveAvatar?.(userId) || avatarDataUriForName(fallbackName);
    };
    const scoreForSide = (side: 'A' | 'B') => {
      if (completed && result) {
        return side === 'A' ? result.aScore : result.bScore;
      }
      return side === 'A' ? (liveStatus?.aScore ?? 0) : (liveStatus?.bScore ?? 0);
    };
    const pprFor = (userId: string) => userId === BYE_USER_ID ? undefined : options.resolveTournamentPPR?.(userId);
    const winnerId = completed ? resolveRoundRobinMatchWinner(result!, match.playerA.userId, match.playerB.userId) : null;
    const buildPlayerBlock = (side: 'A' | 'B') => {
      const isA = side === 'A';
      const userId = isA ? match.playerA.userId : match.playerB.userId;
      const name = userId === BYE_USER_ID ? '—' : (isA ? playerAName : playerBName);
      const isBye = userId === BYE_USER_ID;
      const trailingText = showLiveInfo || completed ? String(scoreForSide(side)) : playerRecord(userId);
      const hasTurn = showLiveInfo && liveStatus?.currentPlayerSide === (isA ? 'A' : 'B');
      const highlightClass = completed && winnerId === userId ? ' winner' : '';
      const turnClass = hasTurn ? ' current-turn' : '';
      const detailParts = [];
      const recordText = playerRecord(userId);
      if (recordText) detailParts.push(`W-L: ${recordText}`);
      const pprText = isBye ? '' : formatPPR(pprFor(userId));
      if (pprText) detailParts.push(pprText);
      const detailLine = detailParts.join(' | ');
      if (isBye) {
        return `
          <div class="tourney-schedule-live-player-row bye">
            <div class="tourney-schedule-live-player-info">
              <div class="tourney-schedule-live-details">
                <span class="tourney-schedule-live-name">${name}</span>
              </div>
            </div>
          </div>
        `;
      }
      return `
        <div class="tourney-schedule-live-player-row${turnClass}${highlightClass}">
          <div class="tourney-schedule-live-player-info">
            <img src="${avatarFor(userId, name)}" alt="${name}'s avatar" class="tourney-schedule-live-avatar" data-rr-player-id="${userId}" data-rr-player-name="${name}" />
            <div class="tourney-schedule-live-details">
              <span class="tourney-schedule-live-name" data-rr-player-id="${userId}" data-rr-player-name="${name}">${name}</span>
              <span class="tourney-schedule-player-ppr">${detailLine}</span>
            </div>
          </div>
          <span class="tourney-schedule-live-score">${trailingText}</span>
        </div>
      `;
    };
    const isSuddenDeathFinal = liveStatus?.phase === 'sudden_death' && liveStatus?.isFinal;
    const indicatorText = isByeMatch
      ? 'Bye'
      : completed
        ? (isSuddenDeathFinal ? 'Final (SD)' : 'Final')
        : showLiveInfo
          ? (roundText || 'Live now')
          : 'vs';
    const indicatorHtml = indicatorText ? `<div class="tourney-schedule-round-indicator">${indicatorText}</div>` : '';
    const liveLineMarkup = `
      <div class="tourney-schedule-live-block${completed ? ' completed' : ''}">
        ${buildPlayerBlock('A')}
        ${indicatorHtml}
        ${buildPlayerBlock('B')}
      </div>
    `;
    const metaParts: string[] = [];
    if (ballDisplay) metaParts.push(`<span class="tourney-schedule-ball-display">Balls left ${ballDisplay}</span>`);
    const metaLine = metaParts.length
      ? `<div class="tourney-schedule-live-meta-line">${metaParts.join('')}</div>`
      : '';
    const statusLabel = isByeMatch ? '' : (completed ? 'Final' : '');
    return {
      html: `${liveLineMarkup}${metaLine}`,
      isLiveOrCompleted: showLiveInfo || completed,
      statusLabel
    };
  }
  private renderPlayerColumn(args: PlayerColumnArgs): string {
    const { userId, name, isBye, resolveAvatar, resolveTournamentPPR } = args;
    if (isBye) {
      return `
        <div class="tourney-schedule-player">
          <div class="tourney-schedule-player-row">
            <span class="tourney-schedule-player-name">${name}</span>
          </div>
        </div>
      `;
    }
    const avatarSrc = resolveAvatar?.(userId) || avatarDataUriForName(name);
    const formattedPPR = formatPPR(resolveTournamentPPR?.(userId));
    return `
      <div class="tourney-schedule-player">
        <div class="tourney-schedule-player-row">
          <img class="tourney-schedule-avatar" src="${avatarSrc}" alt="${name}'s avatar" data-rr-player-id="${userId}" data-rr-player-name="${name}" />
          <span class="tourney-schedule-player-name" data-rr-player-id="${userId}" data-rr-player-name="${name}">${name}</span>
        </div>
        <span class="tourney-schedule-player-ppr">${formattedPPR}</span>
      </div>
    `;
  }

  public updateMatches(matches: RoundRobinMatch[], options: RoundRobinScheduleOptions = {}) {
    if (!this.root) return;
    this.playerProfileHandler = options.onPlayerProfile;
    const resolver = options.resolveName ?? ((id) => id ?? 'Unknown');

    // Save current scroll position before updating
    this.saveScrollPosition();

    // Update each match element with new data
    const matchElements = this.root.querySelectorAll<HTMLElement>('[data-match-index]');
    matchElements.forEach(el => {
      const idx = Number(el.dataset.matchIndex);
      const match = matches.find(m => m.matchIndex === idx);
      if (!match) return;

      // Update the match's HTML content, preserving the element itself
      el.innerHTML = this.renderMatchContent(match, resolver, options);

      // Re-attach click handler if needed
      if (options.onMatchClick && match.playerA.userId !== BYE_USER_ID && match.playerB.userId !== BYE_USER_ID) {
        el.onclick = () => options.onMatchClick?.(match);
      }
    });

    // Restore scroll position after update
    this.restoreScrollPosition();
  }

  private saveScrollPosition() {
    if (this.roundContainer) {
      this.savedScrollLeft = this.roundContainer.scrollLeft;
    }
  }

  private restoreScrollPosition() {
    if (this.roundContainer && this.savedScrollLeft > 0) {
      // Use requestAnimationFrame to ensure DOM is ready before scrolling
      requestAnimationFrame(() => {
        if (this.roundContainer) {
          this.roundContainer.scrollLeft = this.savedScrollLeft;
        }
      });
    }
  }

  private setupCarousel() {
    if (!this.root) return;
    this.roundContainer = this.root.querySelector('.tourney-schedule-rounds') as HTMLDivElement | undefined;
    this.roundElements = Array.from(this.roundContainer?.querySelectorAll<HTMLDivElement>('.tourney-schedule-round') ?? []);
    this.indicatorButtons = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.tourney-schedule-carousel-dot'));
    if (this.roundContainer) {
      const styles = window.getComputedStyle(this.roundContainer);
      this.containerPaddingLeft = parseFloat(styles.paddingLeft || '0');
    } else {
      this.containerPaddingLeft = 0;
    }
    if (this.roundContainer) {
      this.roundContainer.addEventListener('scroll', this.roundScrollHandler);
    }
    this.indicatorButtons.forEach((btn, idx) => {
      btn.onclick = () => this.scrollToRound(idx);
      btn.setAttribute('aria-label', `Round ${idx + 1}`);
    });
    this.activeRoundIndex = 0;
    this.updateActiveDot();
  }

  private cleanupCarousel() {
    if (this.roundContainer) {
      this.roundContainer.removeEventListener('scroll', this.roundScrollHandler);
    }
    this.roundElements = [];
    this.indicatorButtons = [];
    this.activeRoundIndex = 0;
    this.roundContainer = undefined;
    this.containerPaddingLeft = 0;
  }

  private scrollToRound(index: number) {
    const target = this.roundElements[index];
    if (!target || !this.roundContainer) return;
    const containerWidth = this.roundContainer.clientWidth;
    const targetCenter = (target.offsetLeft - this.containerPaddingLeft) + target.clientWidth / 2;
    const scrollTarget = Math.max(0, targetCenter - containerWidth / 2);
    this.roundContainer.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    });
    this.setActiveRound(index);
  }

  private setActiveRound(index: number) {
    const clamped = Math.max(0, Math.min(this.roundElements.length - 1, index));
    if (clamped === this.activeRoundIndex) return;
    this.activeRoundIndex = clamped;
    this.updateActiveDot();
  }

  private updateActiveDot() {
    this.indicatorButtons.forEach((btn, idx) => {
      btn.classList.toggle('active', idx === this.activeRoundIndex);
    });
  }

  private onPlayerProfileClick = (ev: Event) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const el = target.closest<HTMLElement>('[data-rr-player-id]');
    if (!el) return;
    const userId = el.dataset.rrPlayerId;
    const userName = el.dataset.rrPlayerName || userId || '';
    if (!userId || userId === BYE_USER_ID) return;
    if (!this.playerProfileHandler) return;
    ev.stopPropagation();
    ev.preventDefault();
    this.playerProfileHandler?.(userId, userName);
  };

  public destroy() {
    this.cleanupCarousel();
    if (this.root) {
      this.root.removeEventListener('click', this.onPlayerProfileClick, true);
    }
    if (this.root?.parentElement) {
      this.root.parentElement.removeChild(this.root);
    }
    this.root = undefined;
    this.playerProfileHandler = undefined;
  }
}

function groupByRound(matches: RoundRobinMatch[]): Array<[number, RoundRobinMatch[]]> {
  const map = new Map<number, RoundRobinMatch[]>();
  matches.forEach(match => {
    const round = match.round;
    if (!map.has(round)) map.set(round, []);
    map.get(round)!.push(match);
  });
  const sorted = Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  sorted.forEach(([round, rows]) => rows.sort((a, b) => a.matchIndex - b.matchIndex));
  return sorted;
}

function formatPlayer(player: { userId: string; seed: number }, resolver: NameResolver) {
  if (player.userId === BYE_USER_ID) return 'BYE';
  return resolver(player.userId);
}

function formatPPR(value: number | undefined): string {
  if (typeof value === 'number' && isFinite(value)) {
    return `PPR ${value.toFixed(2)}`;
  }
  return 'PPR --';
}

function resolveRoundRobinMatchWinner(
  result: RoundRobinMatchResult,
  playerAId: string,
  playerBId: string
): string | null {
  if (result.winnerUserId === playerAId || result.winnerUserId === playerBId) {
    return result.winnerUserId;
  }
  if (result.aScore > result.bScore) return playerAId;
  if (result.bScore > result.aScore) return playerBId;
  return null;
}
